import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiAnchor,
  FiGrid,
  FiDroplet,
  FiList,
  FiUsers,
  FiActivity,
} from 'react-icons/fi';
import { PageSidebar } from '../../design-system';
import { useAuth } from '../../providers/logincontext';
import { useAppContext } from '../../providers/AppContext';
import ProjectModal from '../../components/modals/projectModal';
import ImportProjectModal from '../../components/modals/ImportProjectModal';
import { getProjects, listWells, listAllProjectSamples } from '../../api/data';
import { Project } from '../../@types/projects/types';
import { Well } from '../../@types/wells/types';
import { Sample, CalculationStrategies } from '../../@types/samples/types';
import {
  analysesWithResult,
  hasComputedResult
} from '../MultiAnalysisPage/analysisCategory';
import styles from './DashboardPage.module.css';
import SkeletonPage from '../../components/skeleton/SkeletonPage';

const ANALYSIS_LABELS: Record<CalculationStrategies, string> = {
  [CalculationStrategies.LIAO]: 'Liao',
  [CalculationStrategies.LAMBDA]: 'Lambda',
  [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: 'Water-Oil',
  [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: 'Gas-Oil',
};

const ANALYSIS_CLASSES: Record<CalculationStrategies, string> = {
  [CalculationStrategies.LIAO]: styles.tagLiao,
  [CalculationStrategies.LAMBDA]: styles.tagLambda,
  [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: styles.tagWaterOil,
  [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: styles.tagGasOil,
};

/* ── Icon shim ── */
type IconProps = { size?: number; color?: string };
const ArrowLeftIcon = FiArrowLeft as React.FC<IconProps>;
const AnchorIcon = FiAnchor as React.FC<IconProps>;
const GridIcon = FiGrid as React.FC<IconProps>;
const DropletIcon = FiDroplet as React.FC<IconProps>;
const ListIcon = FiList as React.FC<IconProps>;
const UsersIcon = FiUsers as React.FC<IconProps>;
const ActivityIcon = FiActivity as React.FC<IconProps>;

/* ── Helpers ── */
const fmt = (v: string | number | null | undefined, fallback = '—') =>
  v != null && v !== '' ? String(v) : fallback;

const fmtNum = (v: number | null | undefined) =>
  v != null ? v.toFixed(4) : null;

type View = 'overview' | 'wells' | 'samples' | 'members';

/* ── Sub-views ── */

const KpiCard = ({ value, label }: { value: string | number; label: string }) => (
  <div className={styles.kpiCard}>
    <div className={styles.kpiValue}>{value}</div>
    <div className={styles.kpiLabel}>{label}</div>
  </div>
);

const OverviewView = ({
  project,
  wells,
  samples,
}: {
  project: Project;
  wells: Well[];
  samples: Sample[];
}) => {
  const sampledWellUids = new Set(samples.map(s => s.well_uid));
  const analyzedSamples = samples.filter(hasComputedResult);

  const infoFields = [
    { label: 'Country', value: project.country },
    { label: 'Block', value: project.block },
    { label: 'Reservoir', value: project.reservoir },
    { label: 'Rock type', value: project.rock_type },
    { label: 'Lab system', value: project.lab_system },
    { label: 'Analysis type', value: project.type_of_analyze },
  ];

  return (
    <>
      <div className={styles.kpiRow}>
        <KpiCard value={wells.length} label="Wells" />
        <KpiCard value={samples.length} label="Samples" />
        <KpiCard value={sampledWellUids.size} label="Wells with samples" />
        <KpiCard value={analyzedSamples.length} label="Analyzed samples" />
        <KpiCard value={project.users?.length ?? 0} label="Members" />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>Project info</div>
        <div className={styles.sectionBody}>
          <ul className={styles.infoList}>
            {infoFields.map(({ label, value }) => (
              <li key={label} className={styles.infoItem}>
                <span className={styles.infoLabel}>{label}</span>
                {value
                  ? <span className={styles.infoValue}>{value}</span>
                  : <span className={styles.infoValueEmpty}>—</span>
                }
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

const WellsView = ({ wells, samples }: { wells: Well[]; samples: Sample[] }) => (
  <div className={styles.section}>
    <div className={styles.sectionHeader}>Wells ({wells.length})</div>
    <div className={styles.sectionBody}>
      {wells.length === 0 ? (
        <p className={styles.emptyRow}>No wells found for this project.</p>
      ) : (
        <ul className={styles.wellList}>
          {wells.map(well => {
            const sampleCount = samples.filter(s => s.well_uid === well.uid).length;
            return (
              <li key={well.uid} className={styles.wellItem}>
                <span
                  className={styles.wellDot}
                  style={well.color ? { background: well.color } : undefined}
                />
                <span className={styles.wellName}>{fmt(well.well_name)}</span>
                <span className={styles.wellMeta}>
                  {sampleCount} sample{sampleCount !== 1 ? 's' : ''}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  </div>
);

const SamplesView = ({ wells, samples }: { wells: Well[]; samples: Sample[] }) => (
  <div className={styles.section}>
    <div className={styles.sectionHeader}>Samples ({samples.length})</div>
    <div className={styles.sectionBody}>
      {samples.length === 0 ? (
        <p className={styles.emptyRow}>No samples found for this project.</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Well</th>
                <th>Depth</th>
                <th>Porosity</th>
                <th>Permeability</th>
                <th>FZI</th>
                <th>Swi</th>
                <th>Analysis</th>
              </tr>
            </thead>
            <tbody>
              {samples.map(sample => {
                const well = wells.find(w => w.uid === sample.well_uid);
                const v = sample.input_sample_values;
                const hasAnalysis = hasComputedResult(sample);

                const cell = (val: string | null | undefined) =>
                  val != null
                    ? <td>{val}</td>
                    : <td className={styles.tdEmpty}>—</td>;

                return (
                  <tr key={sample.uid}>
                    <td>{fmt(sample.sample_code)}</td>
                    <td>{fmt(well?.well_name)}</td>
                    {cell(sample.depth != null
                      ? `${sample.depth}${sample.depth_unit ? ` ${sample.depth_unit}` : ''}`
                      : null
                    )}
                    {cell(fmtNum(v.porosity))}
                    {cell(fmtNum(v.permeability))}
                    {cell(fmtNum(v.fzi))}
                    {cell(fmtNum(v.swi))}
                    <td>
                      {hasAnalysis ? (
                        <div className={styles.analysisTags}>
                          {analysesWithResult(sample).map(strategy => (
                            <span key={strategy} className={`${styles.analysisTag} ${ANALYSIS_CLASSES[strategy]}`}>
                              {ANALYSIS_LABELS[strategy]}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className={styles.statusPending}>Pending</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

const MembersView = ({ project }: { project: Project }) => (
  <div className={styles.section}>
    <div className={styles.sectionHeader}>Members ({project.users?.length ?? 0})</div>
    <div className={styles.sectionBody}>
      {!project.users?.length ? (
        <p className={styles.emptyRow}>No members found.</p>
      ) : (
        <ul className={styles.memberList}>
          {project.users.map(user => (
            <li key={user.uid} className={styles.memberItem}>
              <span className={styles.memberEmail}>{user.email}</span>
              <span className={`${styles.memberRole} ${user.role === 'owner' ? styles.memberRoleOwner : ''}`}>
                {user.role}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

/* ── Page ── */

const DashboardPage = () => {
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [wells, setWells] = useState<Well[]>([]);
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [view, setView] = useState<View>('overview');
  const { currentUser, username } = useAuth();
  const { updateConfigStates } = useAppContext();

  const projectUid = new URLSearchParams(window.location.search).get('project');

  useEffect(() => {
    if (!projectUid) { setNotFound(true); setLoading(false); return; }

    const load = async () => {
      setLoading(true);
      try {
        const projects = await getProjects();
        const found = projects.find(p => p.uid === projectUid) ?? null;
        if (!found) { setNotFound(true); setLoading(false); return; }
        setProject(found);

        const [wellsData, samplesData] = await Promise.all([
          listWells(projectUid),
          listAllProjectSamples(projectUid),
        ]);
        setWells(Array.isArray(wellsData) ? wellsData : []);
        setSamples(Array.isArray(samplesData) ? samplesData : []);
      } catch (err) {
        console.error('Error loading dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [projectUid]);

  const goBack = () => { navigate('/projects'); };

  /* ── Loading state ── */
  if (loading) {
    return <SkeletonPage />;
  }

  /* ── Not found state ── */
  if (notFound || !project) {
    return (
      <div className={styles.shell}>
        <div className={styles.notFound}>
          <AnchorIcon size={40} color="#A7B7AF" />
          <p className={styles.notFoundTitle}>Project not found</p>
          <button className={styles.backButton} onClick={goBack}>
            <ArrowLeftIcon size={14} /> Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      {/* ── Sidebar ── */}
      <PageSidebar
        className={styles.sidebar}
        projectName={project.project_name ?? undefined}
        onBackToProjects={goBack}
        onNewProject={() => { updateConfigStates({ showProject: true }); }}
        onImportProject={() => { updateConfigStates({ showImportProject: true }); }}
        onProjectSettings={() => { navigate('/projects'); }}
        sections={[
          {
            label: 'Dashboard',
            items: [
              { id: 'overview', label: 'Overview', icon: <GridIcon size={15} />, active: view === 'overview', onClick: () => setView('overview') },
              { id: 'wells', label: 'Wells', icon: <DropletIcon size={15} />, active: view === 'wells', onClick: () => setView('wells') },
              { id: 'samples', label: 'Samples', icon: <ListIcon size={15} />, active: view === 'samples', onClick: () => setView('samples') },
              { id: 'samples-summary', label: 'Samples Summary', icon: <GridIcon size={15} />, onClick: () => { navigate(`/samples-summary?project=${projectUid}`); } },
              { id: 'members', label: 'Members', icon: <UsersIcon size={15} />, active: view === 'members', onClick: () => setView('members') },
            ],
          },
          {
            label: 'Analysis',
            items: [
              {
                id: 'capillary-pressure',
                label: 'Capillary Pressure',
                icon: <ActivityIcon size={15} />,
                onClick: () => { navigate(`/capillary-pressure?project=${projectUid}`); },
              },
            ],
          },
        ]}
        user={currentUser ? {
          name: username ?? currentUser.email.split('@')[0],
        } : undefined}
      />

      {/* ── Main ── */}
      <main className={styles.main}>
        {view === 'overview' && (
          <OverviewView project={project} wells={wells} samples={samples} />
        )}
        {view === 'wells' && (
          <WellsView wells={wells} samples={samples} />
        )}
        {view === 'samples' && (
          <SamplesView wells={wells} samples={samples} />
        )}
        {view === 'members' && (
          <MembersView project={project} />
        )}
      </main>
      <ProjectModal />
      <ImportProjectModal />
    </div>
  );
};

export default DashboardPage;
